import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../api/auth';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');
    const storedOrg = localStorage.getItem('organization');
    const selectedOrgId = localStorage.getItem('selectedOrgId');

    if (storedToken && storedUser) {
      setToken(storedToken);
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      
      if (storedOrg) {
        const parsedOrg = JSON.parse(storedOrg);
        
        // If there's a selected org ID and it's different from stored org,
        // SuperAdmin has switched orgs - keep the selection
        if (selectedOrgId && selectedOrgId !== parsedOrg.id && parsedUser.role === 'SuperAdmin') {
          // Fetch the selected org details - for now use stored org but with updated ID
          // In a real app, you'd fetch the org details from API
          setOrganization({ ...parsedOrg, id: selectedOrgId });
        } else {
          setOrganization(parsedOrg);
        }
      }
    }
    setLoading(false);
  }, []);

  const login = (authResponse) => {
    localStorage.removeItem('actionDialogHiddenUntilLogin');
    localStorage.setItem('accessToken', authResponse.accessToken);
    localStorage.setItem('user', JSON.stringify(authResponse.user));
    if (authResponse.organization) {
      localStorage.setItem('organization', JSON.stringify(authResponse.organization));
      setOrganization(authResponse.organization);
    }
    setToken(authResponse.accessToken);
    setUser(authResponse.user);
  };

  const logout = () => {
    localStorage.removeItem('actionDialogHiddenUntilLogin');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('organization');
    localStorage.removeItem('selectedOrgId');
    // Bug_ID_79: Also clear OrgContext's localStorage items
    localStorage.removeItem('currentOrgId');
    localStorage.removeItem('currentOrgSlug');
    setToken(null);
    setUser(null);
    setOrganization(null);
  };

  const switchOrganization = (newOrg) => {
    // Update organization in state and localStorage
    localStorage.setItem('organization', JSON.stringify(newOrg));
    localStorage.setItem('selectedOrgId', newOrg.id);
    setOrganization(newOrg);
    
    // Update user's orgId to match
    const updatedUser = { ...user, orgId: newOrg.id };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const updateOrganization = (updatedOrgData) => {
    // Update organization data without changing user's orgId (for settings updates)
    const newOrg = { ...organization, ...updatedOrgData };
    localStorage.setItem('organization', JSON.stringify(newOrg));
    setOrganization(newOrg);
  };
  
  const updateUser = (updatedUserData) => {
    const newUser = { ...user, ...updatedUserData };
    localStorage.setItem('user', JSON.stringify(newUser));
    setUser(newUser);
  };

  const value = {
    user,
    token,
    organization,
    loading,
    login,
    logout,
    switchOrganization,
    updateOrganization,
    updateUser,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'SuperAdmin',
    isTenantAdmin: user?.role === 'TenantAdmin' || user?.role === 'SuperAdmin',
    isInstructor: user?.role === 'Instructor',
    isLearner: user?.role === 'Learner',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
